import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import Layout from '@/components/Layout';
import ProtectedRoute from '@/components/ProtectedRoute';
import { ThemeProvider } from '@/components/theme-provider';
import { Toaster } from '@/components/ui/toaster';

// Pages
import AdminPage from '@/pages/AdminPage';
import FacilitiesPage from '@/pages/FacilitiesPage';
import FacilityDashboardPage from '@/pages/FacilityDashboardPage';
import HomePage from '@/pages/HomePage';
import LoginPage from '@/pages/LoginPage';
import NotFoundPage from '@/pages/NotFoundPage';
import TemplatesPage from '@/pages/TemplatesPageSimple';
import LogosUploaderPage from '@/pages/LogosUploaderPage';
import SlideshowsPage from '@/pages/SlideshowsPage';
import SlideshowDetailPage from '@/pages/SlideshowDetailPage';
import SchedulesPage from '@/pages/SchedulesPage';
import OverlaysPage from '@/pages/OverlaysPage';
import OverlayEditorPage from '@/pages/OverlayEditorPage';
import YouTubePage from '@/pages/YouTubePage';
import FacilitySettingsPage from '@/pages/FacilitySettingsPage';
import { useAuth } from '@/store/auth';
import FleetStreamsPage from '@/pages/FleetStreamsPage';
import ScheduleHealthPage from '@/pages/ScheduleHealthPage';
import AboutOverlaysPage from '@/pages/AboutOverlaysPage';
import AboutStreamingPage from '@/pages/AboutStreamingPage';

// Components
import AdminAssetsRedirect from '@/components/AdminAssetsRedirect';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: any) => {
        if (error?.status === 401 || error?.status === 403) {
          return false;
        }
        return failureCount < 3;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

function AppRoutes() {
  const { isAuthenticated, user } = useAuth();

  const getDefaultRoute = () => {
    if (!user) return '/slideshows';
    return user.role === 'admin' ? '/admin' : '/slideshows';
  };

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/about/overlays" element={<AboutOverlaysPage />} />
      <Route path="/about/streaming" element={<AboutStreamingPage />} />
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <LoginPage />}
      />

      {/* ── Slideshows — both roles ── */}
      <Route
        path="/slideshows"
        element={
          <ProtectedRoute allowedRoles={['admin', 'facility']}>
            <Layout><SlideshowsPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/slideshows/:id"
        element={
          <ProtectedRoute allowedRoles={['admin', 'facility']}>
            <Layout><SlideshowDetailPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Schedule — both roles ── */}
      <Route
        path="/schedules"
        element={
          <ProtectedRoute allowedRoles={['admin', 'facility']}>
            <Layout><SchedulesPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* ── Admin-only routes ── */}
      <Route
        path="/logos"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><LogosUploaderPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><AdminAssetsRedirect /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><TemplatesPage /></Layout>
          </ProtectedRoute>
        }
      />
      {/* Fleet stream views behind the dashboard tiles — admin only */}
      <Route
        path="/streams/live"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><FleetStreamsPage mode="live" /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/streams/upcoming"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><FleetStreamsPage mode="upcoming" /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/streams/schedules"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><ScheduleHealthPage /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/overlays"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><OverlaysPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/overlays/:compositionId"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <OverlayEditorPage />
          </ProtectedRoute>
        }
      />

      {/* YouTube — admin only */}
      <Route
        path="/youtube"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><YouTubePage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Facility Settings page */}
      <Route
        path="/facilities/:id/settings"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><FacilitySettingsPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><AdminPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/facilities"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout><FacilitiesPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Facility routes */}
      <Route
        path="/facility-dashboard"
        element={
          <ProtectedRoute allowedRoles={['facility']}>
            <Layout><FacilityDashboardPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark" storageKey="csm-theme-v2">
        <Router>
          <div className="min-h-screen bg-background font-sans antialiased">
            <AppRoutes />
            <Toaster />
          </div>
        </Router>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
